import React, { ReactNode } from "react";
import { Button } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import styled from "styled-components";

interface ContainerProps {
  buttonCount: number;
  active: boolean;
}

interface ButtonInfo {
  label: string;
  content?: ReactNode;
  icon?: React.ComponentType;
  onClick: () => void;
}

const buttonMaker = (radius: string) => {
  return createThemedComponent(Button, {
    Button_borderRadius: radius,
    Button_paddingHorizontal_medium: "26px",
    Button_backgroundColor_themePrimary: "#FFF",
    Button_color_themePrimary: (props: any) =>
      props.theme.vstui.color.primary.getShade(6),
  });
};

const Container = styled.div`
  width: 100%;
  display: flex;
`;

const ButtonContainer = styled.div<ContainerProps>`
  ${props => `width: calc(${100 / props.buttonCount}% + 1px)`};
  ${props =>
    props.active &&
    `
    button { 
      background: ${props.theme.vstui.color.primary.getShade(7)}; 
      color: #fff;
    }
  `}

  margin-right: -1px;
  button {
    position: relative;
    &:focus {
      z-index: 1;
    }
  }
`;

const RoundLeft = buttonMaker("4px 0 0 4px");
const MidButton = buttonMaker("0 0 0 0");
const RoundRight = buttonMaker("0 4px 4px 0");

const LeftButton = ({
  btn,
  active,
  buttonCount,
}: {
  btn: ButtonInfo;
  active: boolean;
  buttonCount: number;
}) => {
  return (
    <ButtonContainer active={active} buttonCount={buttonCount}>
      <RoundLeft
        onClick={btn.onClick}
        iconStart={btn.icon ? <btn.icon /> : undefined}
        label={btn.label}
        fullWidth
      >
        {btn.content}
      </RoundLeft>
    </ButtonContainer>
  );
};

const RightButton = ({
  btn,
  active,
  buttonCount,
}: {
  btn: ButtonInfo;
  active: boolean;
  buttonCount: number;
}) => {
  return (
    <ButtonContainer active={active} buttonCount={buttonCount}>
      <RoundRight
        onClick={btn.onClick}
        label={btn.label}
        fullWidth
        iconStart={btn.icon ? <btn.icon /> : undefined}
      >
        {btn.content}
      </RoundRight>
    </ButtonContainer>
  );
};

const ButtonStrip = ({
  buttons = [],
  selectedButton,
}: {
  buttons: Array<ButtonInfo>;
  selectedButton: number;
}) => {
  const renderMiddleButton = (btn: ButtonInfo, index: number) => {
    return (
      <ButtonContainer
        key={btn.label}
        active={index === selectedButton}
        buttonCount={buttons.length}
        onClick={btn.onClick}
      >
        <MidButton label={btn.label} fullWidth>
          {btn.content}
        </MidButton>
      </ButtonContainer>
    );
  };

  return (
    <Container>
      {buttons.map((baseButton, index) => {
        const singleButton = buttons.length === 1;
        const leftButton = index === 0;
        const rightButton = index === buttons.length - 1;
        const buttonCount = buttons.length;

        const btn = {
          ...baseButton,
          onClick: () => baseButton.onClick(),
        };

        if (singleButton) {
          return (
            <ButtonContainer
              key={btn.label}
              active={index === selectedButton}
              buttonCount={buttonCount}
            >
              <Button fullWidth>{btn.content}</Button>
            </ButtonContainer>
          );
        } else {
          if (leftButton) {
            return (
              <LeftButton
                key={btn.label}
                btn={btn}
                active={index === selectedButton}
                buttonCount={buttonCount}
              />
            );
          } else if (rightButton) {
            return (
              <RightButton
                key={btn.label}
                btn={btn}
                active={index === selectedButton}
                buttonCount={buttonCount}
              />
            );
          }
          return renderMiddleButton(btn, index);
        }
      })}
    </Container>
  );
};

export default ButtonStrip;
